import {
  Component,
  OnDestroy,
  ChangeDetectorRef,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';

import { HttpErrorResponse } from '@angular/common/http';


import Swal from 'sweetalert2';

import { AuthService } from '../../../core/services/auth.service';
import { RouterLink } from '@angular/router';
@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './login.html',
  styleUrl: './login.css',
})
export class Login implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);

  private modalCarregamentoAberto = false;
  private destruido = false;

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    senha: ['', Validators.required],
  });

  readonly loading = signal(false);
  erro = '';

  private atualizarTela(): void {
    if (!this.destruido) {
      this.cdr.detectChanges();
    }
  }

  entrar(): void {
    if (this.loading() || !this.isBrowser) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.atualizarTela();

      const email = this.form.controls.email;
      const senha = this.form.controls.senha;

      let mensagem = 'Verifique os campos informados.';

      if (email.hasError('required')) {
        mensagem = 'Informe seu e-mail para continuar.';
      } else if (email.hasError('email')) {
        mensagem = 'Informe um endereço de e-mail válido.';
      } else if (senha.hasError('required')) {
        mensagem = 'Informe sua senha para continuar.';
      }

      void Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: mensagem,
        confirmButtonText: 'Entendi',
        confirmButtonColor: '#064F82',
      });

      return;
    }

    this.loading.set(true);
    this.erro = '';

    const { email, senha } = this.form.getRawValue();

    this.abrirCarregamento();

    this.auth.login(email.trim(), senha).subscribe({
      next: () => {


        this.finalizarCarregamento();
        void this.auth.redirectAfterLogin();
      },

      error: (error: HttpErrorResponse) => {
        this.finalizarCarregamento();
        this.exibirErroLogin(error);
      },
    });
  }

  private abrirCarregamento(): void {
    if (!this.isBrowser) {
      return;
    }

    this.modalCarregamentoAberto = true;

    void Swal.fire({
      title: 'Autenticando...',
      text: 'Estamos verificando suas credenciais.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  }

  private fecharCarregamento(): void {
    if (!this.isBrowser || !this.modalCarregamentoAberto) {
      return;
    }

    this.modalCarregamentoAberto = false;
    Swal.close();
  }

  private exibirErroLogin(error: HttpErrorResponse): void {
    if (!this.isBrowser) {
      return;
    }

    let titulo = 'Não foi possível entrar';
    let mensagem = 'Ocorreu um erro ao tentar acessar sua conta. Tente novamente.';

    if (error.status === 400 || error.status === 401) {
      titulo = 'Credenciais inválidas';
      mensagem =
        'O e-mail ou a senha informados estão incorretos. Verifique seus dados e tente novamente.';
    } else if (error.status === 403) {
      titulo = 'Acesso não autorizado';
      mensagem = 'Sua conta não possui permissão para acessar a plataforma.';
    } else if (error.status === 0) {
      titulo = 'Erro de conexão';
      mensagem =
        'Não foi possível conectar ao servidor do CuidaZap. Verifique sua conexão ou tente novamente mais tarde.';
    } else if (error.status === 429) {
      titulo = 'Muitas tentativas';
      mensagem =
        'Foram realizadas muitas tentativas de acesso. Tente novamente em alguns instantes.';
    } else if (error.status >= 500) {
      titulo = 'Serviço indisponível';
      mensagem =
        'Nosso servidor está temporariamente indisponível. Tente novamente em alguns instantes.';
    }

    this.erro = mensagem;
    this.atualizarTela();

    void Swal.fire({
      icon: 'error',
      title: titulo,
      text: mensagem,
      confirmButtonText: 'Tentar novamente',
      confirmButtonColor: '#064F82',
      allowOutsideClick: false,
      customClass: {
        popup: 'cuidazap-alert',
      },
    });
  }

  private finalizarCarregamento(): void {
    this.loading.set(false);
    this.fecharCarregamento();
    this.atualizarTela();
  }

  ngOnDestroy(): void {
    this.destruido = true;

    if (this.isBrowser) {
      this.fecharCarregamento();
    }
  }
}
